<template>
  <div class="rsv2-page">

    <!-- ===================================================== -->
    <!-- HERO HEADER                                           -->
    <!-- ===================================================== -->
    <header class="rsv2-hero">
      <div class="rsv2-hero-grid">
        <div class="rsv2-hero-main">
          <div class="rsv2-hero-title">
            STORE REPORT
            <span v-if="filteredItems.length > 0" class="rsv2-count-pill">
              {{ filteredItems.length }}
            </span>
          </div>
        </div>
      </div>
    </header>

    <!-- ===================================================== -->
    <!-- VIEW TOGGLE (Summary / Per Invoice + Status modes)    -->
    <!-- ===================================================== -->
    <section class="rsv2-card rsv2-viewcard">
      <div class="rsv2-view-row">
        <div class="rsv2-view-block">
          <small class="rsv2-label">
            <b-icon icon="table"></b-icon> REPORT VIEW
          </small>
          <div class="rsv2-pillgroup">
            <button
              class="rsv2-pill-btn"
              :class="{ 'is-active': details_filter === 1 }"
              @click="switchReportView(1)"
            >
              <b-icon icon="bar-chart"></b-icon> Summary
            </button>
            <button
              class="rsv2-pill-btn"
              :class="{ 'is-active': details_filter === 4 }"
              @click="switchReportView(4)"
            >
              <b-icon icon="receipt"></b-icon> Per Invoice
            </button>
          </div>
        </div>

        <div class="rsv2-view-block">
          <small class="rsv2-label">
            <b-icon icon="folder"></b-icon> RECORD STATUS
          </small>
          <div class="rsv2-pillgroup">
            <button
              v-for="v in viewModes"
              :key="v.id"
              class="rsv2-pill-btn"
              :class="{ 'is-active-dark': view_mode === v.id }"
              @click="setViewMode(v.id)"
            >
              <b-icon :icon="v.icon"></b-icon>
              {{ v.label }}
              <span class="rsv2-pill-count">{{ v.count }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Drill-down breadcrumb -->
      <transition name="rsv2-fade">
        <div v-if="details_filter === 4 && drilled_location_id !== null" class="rsv2-drillbar">
          <span class="rsv2-chip is-teal">
            <b-icon icon="geo-alt-fill"></b-icon>
            {{ drilledLocationName }}
          </span>
          <button class="rsv2-linkbtn" @click="backToSummary">
            <b-icon icon="arrow-left"></b-icon> Back to Summary
          </button>
        </div>
      </transition>
    </section>

    <!-- ===================================================== -->
    <!-- FILTERS                                               -->
    <!-- ===================================================== -->
    <section class="rsv2-card rsv2-filtercard">
      <div class="rsv2-filters-row">
        <div class="rsv2-fb-item rsv2-fb-dates">
          <b-input-group class="rsv2-inputgroup">
            <b-input-group-prepend>
              <b-input-group-text><b-icon icon="calendar-range"></b-icon></b-input-group-text>
            </b-input-group-prepend>
            <date-picker
              v-model="date_range"
              format="MM-DD-YYYY"
              valueType="format"
              class="rsv2-date-input"
              placeholder="Select Date Range"
              range
            />
          </b-input-group>
        </div>

        <div class="rsv2-quickdates">
          <button
            v-for="btn in quickDateButtons"
            :key="btn.id"
            class="rsv2-quick-btn"
            :class="{ 'is-active': picked_date_range === btn.id }"
            @click="set_dates(btn.id)"
          >
            {{ btn.label }}
          </button>
        </div>

        <button class="rsv2-btn-primary rsv2-search-btn" @click="fetch_init" :disabled="items_being_loaded">
          <b-icon :icon="items_being_loaded ? 'arrow-repeat' : 'search'" :animation="items_being_loaded ? 'spin' : null"></b-icon>
          {{ items_being_loaded ? 'Loading…' : 'Search' }}
        </button>
      </div>

      <div class="rsv2-advanced-toggle-wrap">
        <button class="rsv2-linkbtn" @click="showAdvancedFilters = !showAdvancedFilters">
          <b-icon :icon="showAdvancedFilters ? 'chevron-up' : 'chevron-down'"></b-icon>
          {{ showAdvancedFilters ? 'Hide' : 'Show' }} advanced filters
        </button>
      </div>

      <b-collapse v-model="showAdvancedFilters">
        <div class="rsv2-advanced-grid">
          <div class="rsv2-adv-item">
            <small class="rsv2-label"><b-icon icon="geo-alt"></b-icon> LOCATION</small>
            <b-form-select v-model="picked_location_id" :options="filteredLocations"
                           class="rsv2-select" @change="fetch_init">
              <template #first>
                <b-form-select-option :value="null">All Locations</b-form-select-option>
              </template>
            </b-form-select>
          </div>

          <div class="rsv2-adv-item">
            <small class="rsv2-label"><b-icon icon="receipt"></b-icon> INVOICE TYPE</small>
            <b-form-select v-model="picked_invoice_type" :options="invoice_options"
                           class="rsv2-select" @change="fetch_init">
              <template #first>
                <b-form-select-option :value="null">All Types</b-form-select-option>
              </template>
            </b-form-select>
          </div>

          <div class="rsv2-adv-item">
            <small class="rsv2-label"><b-icon icon="tag"></b-icon> SALE TYPE</small>
            <b-form-select v-model="picked_sale_type" :options="sale_type_options"
                           class="rsv2-select" @change="fetch_init">
              <template #first>
                <b-form-select-option :value="null">All Sale Types</b-form-select-option>
              </template>
            </b-form-select>
          </div>

          <div class="rsv2-adv-item">
            <small class="rsv2-label"><b-icon icon="credit-card"></b-icon> PAYMENT METHOD</small>
            <b-form-select v-model="picked_payment_method" :options="paymentMethodOptions"
                           class="rsv2-select" @change="fetch_init">
              <template #first>
                <b-form-select-option :value="null">All Payment Methods</b-form-select-option>
              </template>
            </b-form-select>
          </div>
        </div>
      </b-collapse>
    </section>

    <!-- ===================================================== -->
    <!-- LOADING                                               -->
    <!-- ===================================================== -->
    <transition name="rsv2-fade">
      <div v-if="items_being_loaded" class="rsv2-card rsv2-loading-inline">
        <b-spinner variant="primary"></b-spinner>
        <span>Fetching store data…</span>
      </div>
    </transition>

    <!-- ===================================================== -->
    <!-- CONTENT                                               -->
    <!-- ===================================================== -->
    <template v-if="!items_being_loaded && filteredItems.length > 0">

      <!-- ============ SUMMARY KPI CARDS ============ -->
      <transition name="rsv2-fade-up">
        <section v-if="details_filter === 1" class="rsv2-kpi-grid">
          <div class="rsv2-kpi is-total">
            <div class="rsv2-kpi-icon"><b-icon icon="graph-up"></b-icon></div>
            <div class="rsv2-kpi-body">
              <div class="rsv2-kpi-value">{{ summaryStats[0] ? summaryStats[0].value : '$0.00' }}</div>
              <div class="rsv2-kpi-label">Total Sales</div>
            </div>
          </div>
          <div class="rsv2-kpi is-refund">
            <div class="rsv2-kpi-icon"><b-icon icon="arrow-return-left"></b-icon></div>
            <div class="rsv2-kpi-body">
              <div class="rsv2-kpi-value">{{ summaryStats[1] ? summaryStats[1].value : '$0.00' }}</div>
              <div class="rsv2-kpi-label">Total Refunds</div>
            </div>
          </div>
          <div class="rsv2-kpi is-count">
            <div class="rsv2-kpi-icon"><b-icon icon="wallet"></b-icon></div>
            <div class="rsv2-kpi-body">
              <div class="rsv2-kpi-value">{{ summaryStats[2] ? summaryStats[2].value : '$0.00' }}</div>
              <div class="rsv2-kpi-label">Net Amount</div>
            </div>
          </div>
          <div class="rsv2-kpi is-layaway">
            <div class="rsv2-kpi-icon"><b-icon icon="receipt"></b-icon></div>
            <div class="rsv2-kpi-body">
              <div class="rsv2-kpi-value">{{ summaryStats[3] ? summaryStats[3].value : '$0.00' }}</div>
              <div class="rsv2-kpi-label">Sales Tax</div>
            </div>
          </div>
        </section>
      </transition>

      <!-- ============ ACTION BAR ============ -->
      <transition name="rsv2-fade-up">
        <div v-if="details_filter === 4 && selectedInvoiceIds.length > 0" class="rsv2-actionbar">
          <div class="rsv2-actionbar-left">
            <span class="rsv2-chip is-orange">
              <b-icon icon="check-circle"></b-icon>
              {{ selectedInvoiceIds.length }} selected
            </span>
          </div>
          <div class="rsv2-actionbar-right">
            <template v-if="view_mode === 'normal'">
              <button class="rsv2-btn-warn" @click="performAction('archive')">
                <b-icon icon="archive"></b-icon> Archive
              </button>
              <button class="rsv2-btn-danger" @click="performAction('delete')">
                <b-icon icon="trash"></b-icon> Delete
              </button>
            </template>
            <template v-else-if="view_mode === 'archived'">
              <button class="rsv2-btn-success" @click="performAction('unarchive')">
                <b-icon icon="arrow-counterclockwise"></b-icon> Unarchive
              </button>
              <button class="rsv2-btn-danger" @click="performAction('delete')">
                <b-icon icon="trash"></b-icon> Delete
              </button>
            </template>
            <template v-else-if="view_mode === 'deleted'">
              <button class="rsv2-btn-success" @click="performAction('restore')">
                <b-icon icon="arrow-counterclockwise"></b-icon> Restore
              </button>
            </template>
            <button class="rsv2-btn-ghost" @click="clearSelection">
              <b-icon icon="x"></b-icon> Clear
            </button>
          </div>
        </div>
      </transition>

      <!-- ============ DATA TABLE ============ -->
      <section class="rsv2-card rsv2-tablecard">
        <div class="rsv2-table-header">
          <div class="rsv2-table-title">
            <b-icon icon="table"></b-icon>
            {{ getTableTitle() }}
            <span class="rsv2-table-badge">
              {{ filteredItems.length }} {{ filteredItems.length === 1 ? 'record' : 'records' }}
            </span>
          </div>
          <div class="rsv2-table-actions">
            <button v-if="canExport" class="rsv2-btn-ghost" @click="exportToCSV">
              <b-icon icon="download"></b-icon> Export
            </button>
            <button class="rsv2-btn-ghost" @click="fetch_init">
              <b-icon icon="arrow-repeat"></b-icon> Refresh
            </button>
          </div>
        </div>

        <b-overlay :show="items_being_loaded" spinner-variant="primary" spinner-type="grow" rounded="sm">
          <div class="rsv2-table-scroll">
            <table class="rsv2-table">
              <thead>
                <tr>
                  <th v-if="details_filter === 4" class="rsv2-th rsv2-th-check">
                    <b-form-checkbox
                      :checked="allSelected"
                      :indeterminate="someSelected && !allSelected"
                      @change="toggleSelectAll"
                      class="mb-0"
                    />
                  </th>
                  <th v-for="col in allowed_cols()" :key="col"
                      class="rsv2-th"
                      :class="getHeaderClass(col)"
                      :style="getHeaderStyle(col)">
                    <div class="rsv2-th-inner">
                      <span>{{ fixed_columns_dict[col] || col }}</span>
                      <button v-if="isSortableColumn(col)" class="rsv2-sort-btn" @click="sortByColumn(col)">
                        <b-icon
                          :icon="sortColumn === col
                            ? (sortDirection === 'asc' ? 'arrow-up-short' : 'arrow-down-short')
                            : 'arrow-down-up'"
                          :class="{ 'is-active': sortColumn === col }"
                        ></b-icon>
                      </button>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, idx) in paginatedItems"
                  :key="idx"
                  class="rsv2-row"
                  :class="[
                    getRowClass(item),
                    { 'is-clickable': details_filter === 1 }
                  ]"
                  :style="{ animationDelay: (idx * 0.015) + 's' }"
                  @click="onRowClick(item)"
                >
                  <td v-if="details_filter === 4" class="rsv2-td rsv2-td-check" @click.stop>
                    <b-form-checkbox
                      :checked="selectedInvoiceIds.includes(item.invoice_id)"
                      @change="toggleSelect(item.invoice_id)"
                      class="mb-0"
                    />
                  </td>
                  <td v-for="row_col in allowed_cols()" :key="row_col"
                      class="rsv2-td"
                      :class="getCellClass(row_col, item[row_col])">
                    <span v-if="row_col == 'locsub'" class="rsv2-loc-cell">
                      <b-icon icon="geo-alt-fill" class="rsv2-loc-icon"></b-icon>
                      {{ packet.locsubsdict[item[row_col]] || item[row_col] }}
                      <b-icon v-if="details_filter === 1" icon="chevron-right" class="rsv2-drill-icon"></b-icon>
                    </span>
                    <span v-else-if="row_col == 'is_delivered'" class="rsv2-status-chip"
                          :class="item[row_col] ? 'is-teal' : 'is-warn'">
                      <b-icon :icon="item[row_col] ? 'check-circle' : 'clock'"></b-icon>
                      {{ item[row_col] ? 'Delivered' : 'Not Delivered' }}
                    </span>
                    <span v-else-if="row_col == 'payment_methods'">
                      <span
                        v-for="pm in (item[row_col] || [])"
                        :key="pm"
                        class="rsv2-mini-chip"
                      >{{ pm }}</span>
                      <span v-if="!(item[row_col] || []).length" class="rsv2-muted">—</span>
                    </span>
                    <a
                      v-else-if="row_col === 'invoice_id' && item[row_col]"
                      href="javascript:void(0)"
                      class="rsv2-invoice-link"
                      :title="'Open invoice ' + item[row_col] + ' in a new tab'"
                      @click.stop.prevent="openInvoiceInNewTab(item[row_col])"
                    >
                      {{ item[row_col] }}
                      <b-icon icon="box-arrow-up-right" class="rsv2-invoice-link-icon"></b-icon>
                    </a>
                    <span v-else-if="row_col === 'invoice_id'">—</span>
                    <span v-else-if="isNumericColumn(row_col)" class="rsv2-num">
                      {{ formatCurrency(item[row_col]) }}
                    </span>
                    <span v-else>{{ item[row_col] !== null && item[row_col] !== undefined ? item[row_col] : '—' }}</span>
                  </td>
                </tr>
              </tbody>
              <tfoot v-if="showFooter">
                <tr class="rsv2-totals-row">
                  <td v-if="details_filter === 4" class="rsv2-td-check"></td>
                  <td v-for="col in allowed_cols()" :key="'ft-' + col"
                      class="rsv2-td"
                      :class="getHeaderClass(col)">
                    <span v-if="col === 'locsub'" class="rsv2-totals-label">TOTAL</span>
                    <span v-else-if="isNumericColumn(col)" class="rsv2-totals-amt">
                      {{ formatCurrency(columnTotal(col)) }}
                    </span>
                    <span v-else>—</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div v-if="filteredItems.length > perPage" class="rsv2-pagination">
            <small class="rsv2-page-info">
              Showing {{ ((currentPage - 1) * perPage) + 1 }}–{{ Math.min(currentPage * perPage, filteredItems.length) }}
              of {{ filteredItems.length }}
            </small>
            <b-pagination v-model="currentPage" :total-rows="filteredItems.length"
                          :per-page="perPage" size="sm" class="mb-0" align="center" />
            <b-form-select v-model="perPage" size="sm" style="width: 90px;" @change="currentPage = 1">
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </b-form-select>
          </div>
        </b-overlay>
      </section>
    </template>

    <!-- ============ EMPTY STATES ============ -->
    <transition name="rsv2-fade">
      <div v-if="!items_being_loaded && items.length === 0" class="rsv2-card rsv2-empty">
        <div class="rsv2-empty-icon"><b-icon icon="inbox" font-scale="2.4"></b-icon></div>
        <h3>No records in {{ view_mode }} view</h3>
        <p>Try a different date range or switch record status.</p>
      </div>
    </transition>

    <transition name="rsv2-fade">
      <div v-if="!items_being_loaded && filteredItems.length === 0 && items.length > 0" class="rsv2-card rsv2-empty">
        <div class="rsv2-empty-icon"><b-icon icon="funnel" font-scale="2.4"></b-icon></div>
        <h3>No data matches your filters</h3>
        <p>Adjust the filters above and try again.</p>
      </div>
    </transition>

    <!-- ===================================================== -->
    <!-- CONFIRM ACTION MODAL                                  -->
    <!-- ===================================================== -->
    <b-modal
      v-model="confirmModal.show"
      centered
      no-close-on-backdrop
      hide-header-close
      content-class="rsv2-modal"
      @ok="confirmModal.onConfirm"
    >
      <template #modal-header>
        <div class="rsv2-modal-title">
          <span class="rsv2-modal-title-icon" :class="'is-' + confirmModal.variant">
            <b-icon :icon="confirmModal.icon"></b-icon>
          </span>
          <div>
            <div class="rsv2-modal-title-main">{{ confirmModal.title }}</div>
            <div class="rsv2-modal-title-sub">Please confirm this action</div>
          </div>
        </div>
      </template>

      <div class="rsv2-modal-body">
        <p class="rsv2-modal-message">{{ confirmModal.message }}</p>
        <p v-if="confirmModal.subMessage" class="rsv2-modal-sub">{{ confirmModal.subMessage }}</p>
      </div>

      <template #modal-footer="{ ok, cancel }">
        <button class="rsv2-btn-ghost" @click="cancel()">Cancel</button>
        <button class="rsv2-btn-primary" :class="'is-' + confirmModal.variant" @click="ok()">
          <b-icon :icon="confirmModal.icon"></b-icon>
          {{ confirmModal.okLabel }}
        </button>
      </template>
    </b-modal>

  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import 'moment/locale/en-gb';
import Requestor from '@/utilities/Requestor';

import {
  BIconShop, BIconCalendar, BIconSearch, BIconTable, BIconArrowRepeat,
  BIconDownload, BIconBuilding, BIconTag, BIconInbox, BIconReceipt,
  BIconArrowUpShort, BIconArrowDownShort, BIconFilterCircle,
  BIconChevronUp, BIconChevronDown, BIconGraphUp, BIconGraphDown,
  BIconWallet, BIconCash, BIconPercent, BIconArchive, BIconTrash,
  BIconArrowCounterclockwise, BIconCheckCircle, BIconX, BIconInfoCircle,
  BIconCreditCard, BIconBarChart, BIconFolder, BIconChevronRight,
  BIconArrowLeft, BIconGeoAlt, BIconArrowReturnLeft, BIconArrowDownUp
} from 'bootstrap-vue';

moment.locale('en-gb');

export default {
  name: 'ReportsStoresSaleV2',
  components: {
    DatePicker, BIconShop, BIconCalendar, BIconSearch, BIconTable,
    BIconArrowRepeat, BIconDownload, BIconBuilding, BIconTag, BIconInbox,
    BIconReceipt, BIconArrowUpShort, BIconArrowDownShort, BIconFilterCircle,
    BIconChevronUp, BIconChevronDown, BIconGraphUp, BIconGraphDown,
    BIconWallet, BIconCash, BIconPercent, BIconArchive, BIconTrash,
    BIconArrowCounterclockwise, BIconCheckCircle, BIconX, BIconInfoCircle,
    BIconCreditCard, BIconBarChart, BIconFolder, BIconChevronRight,
    BIconArrowLeft, BIconGeoAlt, BIconArrowReturnLeft, BIconArrowDownUp
  },
  data() {
    return {
      packet: { userDetails: {}, locsubsdict: {}, locations: [] },
      items: [],
      items_being_loaded: false,

      viewCounts: { normal: 0, archived: 0, deleted: 0 },

      date_range: [
        moment().startOf('month').format('MM-DD-YYYY'),
        moment().format('MM-DD-YYYY')
      ],
      picked_date_range: 'this_month',
      showAdvancedFilters: false,

      sale_type_options: [
        { value: null, text: 'ALL SALE TYPES' },
        { value: 'regular', text: 'REGULAR SALES' },
        { value: 'layaway', text: 'LAYAWAY SALES' }
      ],
      picked_sale_type: null,

      invoice_options: [
        { value: null, text: 'ALL' },
        { value: 1, text: 'WRITTEN' },
        { value: 2, text: 'DELIVERED' },
        { value: 3, text: 'NOT DELIVERED' }
      ],
      picked_invoice_type: null,

      picked_payment_method: null,
      payment_methods_from_api: [],
      view_mode: 'normal',
      selectedInvoiceIds: [],

      details_filter: 1,
      picked_location_id: null,

      drilled_location_id: null,
      drilled_location_label: null,

      columns: [],
      fixed_columns_dict: {
        locsub: 'LOCATION',
        total_without_tax: 'TOTAL SALE',
        total_refunded_without_tax: 'TOTAL REFUNDED',
        total_sales_tax: 'SLS TAX',
        total_refunded_st: 'SLS TAX REFUNDED',
        total_cc_sales: 'CC SALES',
        total_finance_sales: 'FIN SALES',
        total_tax_exempt_sales: 'TAX EXEMPT SALES',
        total_cash_sales: 'CASH SALES',
        total_cc_refunds: 'CC REFUNDS',
        total_finance_refunds: 'FIN REFUNDS',
        total_tax_exempt_refunds: 'TAX EXEMPT REFUNDS',
        total_cash_refunds: 'CASH REFUNDS',
        invoice_id: 'INVOICE #',
        customer_fullname: 'CUSTOMER NAME',
        customer_email: 'EMAIL',
        customer_phone: 'PHONE',
        is_delivered: 'DELIVERY STATUS',
        payment_methods: 'PAYMENT METHODS',
        invoice_ts: 'DATE'
      },

      currentPage: 1,
      perPage: 25,
      sortColumn: null,
      sortDirection: 'asc',

      quickDateButtons: [
        { id: 'today', label: 'Today' },
        { id: 'yesterday', label: 'Yesterday' },
        { id: 'this_week', label: 'This Week' },
        { id: 'last_week', label: 'Last Week' },
        { id: 'this_month', label: 'This Month' },
        { id: 'last_month', label: 'Last Month' }
      ],

      confirmModal: {
        show: false,
        title: '',
        message: '',
        subMessage: '',
        icon: 'exclamation-circle',
        variant: 'primary',
        okLabel: 'Confirm',
        onConfirm: () => {}
      }
    };
  },

  computed: {
    formattedDateRange() {
      if (this.date_range && this.date_range.length === 2) {
        return `${this.date_range[0]} → ${this.date_range[1]}`;
      }
      return 'Select dates';
    },

    summarySalesDisplay() {
      if (this.details_filter !== 1) return 'Per-invoice view';
      const s = parseFloat(((this.summaryStats[0] || {}).value || '').replace(/[^0-9.-]/g, '') || 0);
      return `$${s.toLocaleString('en-US', { minimumFractionDigits: 2 })} gross`;
    },

    filteredLocations() {
      if (!this.packet.locations) return [];
      const excluded = ['FOLCROFT WH', '101 WH', '9TH ST WH', 'OUT OF STOCK',
                        'MECCA SHIPPING', 'ONLINE SALES'];
      return this.packet.locations.filter(l => !excluded.includes(l.text));
    },

    paymentMethodOptions() {
      return this.payment_methods_from_api.map(pm => ({
        value: pm, text: pm.toUpperCase()
      }));
    },

    viewModes() {
      return [
        { id: 'normal',   label: 'Normal',   icon: 'check-circle', count: this.viewCounts.normal },
        { id: 'archived', label: 'Archived', icon: 'archive',      count: this.viewCounts.archived },
        { id: 'deleted',  label: 'Deleted',  icon: 'trash',        count: this.viewCounts.deleted }
      ];
    },

    drilledLocationName() {
      return this.drilled_location_label || 'Selected location';
    },

    filteredItems() {
      let filtered = this.items.filter(i => this.allowed_row(i));

      if (this.picked_payment_method) {
        filtered = filtered.filter(i =>
          (i.payment_methods || []).includes(this.picked_payment_method)
        );
      }

      if (this.sortColumn) {
        filtered = filtered.slice().sort((a, b) => {
          let av = a[this.sortColumn];
          let bv = b[this.sortColumn];
          if (this.isNumericColumn(this.sortColumn)) {
            av = parseFloat(av || 0);
            bv = parseFloat(bv || 0);
          }
          if (this.sortColumn === 'locsub') {
            av = this.packet.locsubsdict[av] || av;
            bv = this.packet.locsubsdict[bv] || bv;
          }
          if (av < bv) return this.sortDirection === 'asc' ? -1 : 1;
          if (av > bv) return this.sortDirection === 'asc' ? 1 : -1;
          return 0;
        });
      }
      return filtered;
    },

    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredItems.slice(start, start + this.perPage);
    },

    summaryStats() {
      if (this.details_filter !== 1 || this.filteredItems.length === 0) return [];
      const t = this.filteredItems.reduce((a, i) => {
        a.sales   += parseFloat(i.total_without_tax || 0);
        a.refunds += parseFloat(i.total_refunded_without_tax || 0);
        a.tax     += parseFloat(i.total_sales_tax || 0);
        return a;
      }, { sales: 0, refunds: 0, tax: 0 });
      return [
        { label: 'Total Sales',   value: this.formatCurrency(t.sales) },
        { label: 'Total Refunds', value: this.formatCurrency(t.refunds) },
        { label: 'Net Amount',    value: this.formatCurrency(t.sales - t.refunds) },
        { label: 'Sales Tax',     value: this.formatCurrency(t.tax) }
      ];
    },

    showFooter() {
      return this.filteredItems.length > 0;
    },

    allSelected() {
      if (this.details_filter !== 4) return false;
      const visibleIds = this.paginatedItems
        .map(i => i.invoice_id)
        .filter(id => id != null && id !== '');
      if (!visibleIds.length) return false;
      return visibleIds.every(id => this.selectedInvoiceIds.includes(id));
    },

    someSelected() {
      const visibleIds = this.paginatedItems
        .map(i => i.invoice_id)
        .filter(id => id != null && id !== '');
      return visibleIds.some(id => this.selectedInvoiceIds.includes(id));
    },

    canExport() {
      return this.filteredItems.length > 0;
    }
  },

  watch: {
    filteredItems() { this.currentPage = 1; },
    view_mode() {
      this.selectedInvoiceIds = [];
      this.fetch_init();
    },
    details_filter() {
      this.selectedInvoiceIds = [];
      this.currentPage = 1;
    }
  },

  methods: {
    formatCurrency(v) {
      const n = parseFloat(v || 0);
      if (isNaN(n)) return '$0.00';
      return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    },

    isNumericColumn(col) {
      const nonNum = ['locsub', 'invoice_id', 'is_delivered', 'payment_methods',
                      'customer_fullname', 'customer_email', 'customer_phone',
                      'invoice_ts', 'is_archived', 'is_deleted',
                      'location_id', 'sublocation_id',
                      'frg_location_id', 'frg_sublocation_id',
                      'fsg_location_id', 'fsg_sublocation_id'];
      if (col.startsWith('customer_')) return false;
      return !nonNum.includes(col);
    },

    allowed_row(item) {
      if (this.picked_location_id != null &&
          item.location_id != this.picked_location_id) return false;
      return true;
    },

    allowed_cols() {
      if (!this.columns.length) return [];

      if (this.details_filter === 4) {
        const wanted = ['invoice_id', 'invoice_ts', 'customer_fullname',
                        'is_delivered', 'locsub', 'total_without_tax',
                        'total_refunded_without_tax', 'total_sales_tax',
                        'total_refunded_st', 'payment_methods'];
        return wanted.filter(c => this.columns.includes(c));
      }

      const summaryCols = [
        'locsub',
        'total_without_tax',
        'total_refunded_without_tax',
        'total_sales_tax',
        'total_refunded_st',
        'total_cash_sales',
        'total_cc_sales',
        'total_finance_sales',
        'total_tax_exempt_sales'
      ];
      return summaryCols.filter(c => this.columns.includes(c));
    },

    getTableTitle() {
      if (this.details_filter === 4) {
        return this.view_mode === 'archived' ? 'Per Invoice (Archived)'
             : this.view_mode === 'deleted'  ? 'Per Invoice (Deleted)'
             : this.drilled_location_id !== null ? `Per Invoice — ${this.drilledLocationName}`
             : 'Per Invoice';
      }
      return this.view_mode === 'archived' ? 'Sales Summary (Archived)'
           : this.view_mode === 'deleted'  ? 'Sales Summary (Deleted)'
           : 'Sales Summary';
    },

    onRowClick(item) {
      if (this.details_filter !== 1) return;
      if (!item || item.locsub == null) return;

      const parts = String(item.locsub).split('|');
      if (parts.length < 2) return;
      const location_id = parseInt(parts[0], 10);

      this.drilled_location_id = location_id;
      this.drilled_location_label =
        this.packet.locsubsdict[item.locsub] || item.locsub;

      this.details_filter = 4;
      this.selectedInvoiceIds = [];
      this.currentPage = 1;
      this.sortColumn = null;

      this.fetch_init();
    },

    backToSummary() {
      this.drilled_location_id = null;
      this.drilled_location_label = null;
      this.details_filter = 1;
      this.selectedInvoiceIds = [];
      this.currentPage = 1;
      this.sortColumn = null;
      this.fetch_init();
    },

    getHeaderClass(c) { return this.isNumericColumn(c) ? 'is-right' : 'is-center'; },
    getHeaderStyle(c) {
      if (c === 'locsub') return 'min-width: 220px;';
      if (c === 'invoice_id' || c === 'payment_methods') return 'min-width: 150px;';
      if (c.startsWith('customer_')) return 'min-width: 150px;';
      if (c === 'is_delivered') return 'min-width: 130px;';
      return 'min-width: 110px;';
    },
    getCellClass(c, v) {
      const cls = [];
      if (this.isNumericColumn(c)) cls.push('is-right');
      if (c.includes('refund') && parseFloat(v) > 0) cls.push('is-danger');
      if (c.includes('sales') && parseFloat(v) > 0) cls.push('is-teal');
      return cls.join(' ');
    },
    getRowClass(item) {
      const net = parseFloat(item.total_without_tax || 0) -
                  parseFloat(item.total_refunded_without_tax || 0);
      if (net < 0) return 'is-danger-row';
      if (net > 10000) return 'is-teal-row';
      return '';
    },
    isSortableColumn(c) { return !['payment_methods', 'is_delivered'].includes(c); },
    sortByColumn(c) {
      if (this.sortColumn === c) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = c;
        this.sortDirection = 'asc';
      }
    },
    columnTotal(c) {
      if (!this.isNumericColumn(c)) return 0;
      return this.filteredItems.reduce(
        (s, i) => s + (parseFloat(i[c] || 0) || 0), 0
      );
    },

    switchReportView(mode) {
      if (this.details_filter === mode && this.drilled_location_id === null) return;
      this.drilled_location_id = null;
      this.drilled_location_label = null;
      this.details_filter = mode;
      this.selectedInvoiceIds = [];
      this.currentPage = 1;
      this.sortColumn = null;
      this.fetch_init();
    },

    set_dates(value) {
      let s, e;
      switch (value) {
        case 'today':      s = e = moment().format('MM-DD-YYYY'); break;
        case 'yesterday':  s = e = moment().add(-1, 'd').format('MM-DD-YYYY'); break;
        case 'this_week':
          s = moment().startOf('week').format('MM-DD-YYYY');
          e = moment().endOf('week').format('MM-DD-YYYY');
          break;
        case 'last_week':
          s = moment().subtract(1, 'w').startOf('week').format('MM-DD-YYYY');
          e = moment().subtract(1, 'w').endOf('week').format('MM-DD-YYYY');
          break;
        case 'this_month':
          s = moment().startOf('month').format('MM-DD-YYYY');
          e = moment().endOf('month').format('MM-DD-YYYY');
          break;
        case 'last_month':
          s = moment().subtract(1, 'M').startOf('month').format('MM-DD-YYYY');
          e = moment().subtract(1, 'M').endOf('month').format('MM-DD-YYYY');
          break;
      }
      if (s && e) {
        this.date_range = [s, e];
        this.picked_date_range = value;
        this.fetch_init();
      }
    },

    setViewMode(mode) {
      if (this.view_mode === mode) return;
      this.view_mode = mode;
    },

    // -------- NEW: open invoice in new tab --------
    openInvoiceInNewTab(invoice_id) {
      if (!invoice_id) return;
      const routeData = this.$router.resolve({
        path: `/sales/viewsale/${encodeURIComponent(invoice_id)}/`
      });
      window.open(routeData.href, '_blank', 'noopener,noreferrer');
    },

    // -------- selection --------
    toggleSelect(id) {
      if (id == null || id === '') return;
      const i = this.selectedInvoiceIds.indexOf(id);
      if (i === -1) this.selectedInvoiceIds.push(id);
      else this.selectedInvoiceIds.splice(i, 1);
    },
    toggleSelectAll(checked) {
      const ids = this.paginatedItems
        .map(i => i.invoice_id)
        .filter(id => id != null && id !== '');
      if (checked) {
        ids.forEach(id => {
          if (!this.selectedInvoiceIds.includes(id)) this.selectedInvoiceIds.push(id);
        });
      } else {
        this.selectedInvoiceIds = this.selectedInvoiceIds.filter(id => !ids.includes(id));
      }
    },
    clearSelection() { this.selectedInvoiceIds = []; },

    // -------- fetch --------
    fetch_init() {
      if (!this.date_range || this.date_range.length !== 2) {
        this.$bvToast.toast('Please select a date range.',
          { title: 'Invalid Date Range', variant: 'warning', solid: true });
        return;
      }

      let location_id = this.packet.userDetails.properties
        && this.packet.userDetails.properties.location_id;
      if (this.packet.userDetails.userrole === 'admin') location_id = null;

      if (this.drilled_location_id !== null) {
        location_id = this.drilled_location_id;
      }

      const payload = {
        dates: this.date_range,
        location_id,
        picked_invoice_type: this.picked_invoice_type,
        details_filter: this.details_filter,
        sale_type: this.picked_sale_type,
        payment_method: this.picked_payment_method,
        view_mode: this.view_mode,
        username: this.packet.userDetails.username
      };

      const that = this;
      const s = (resp) => {
        that.items_being_loaded = false;
        const body = resp && resp.data;
        if (body == null) {
          that.$bvToast.toast('Server returned empty response. Check Flask logs.',
            { title: 'Error', variant: 'danger', solid: true });
          that.items = [];
          that.columns = [];
          return;
        }
        if (body.error) {
          that.$bvToast.toast(body.error, { title: 'Error', variant: 'danger' });
          that.items = [];
          that.columns = [];
          return;
        }
        that.items = body.result || [];
        that.columns = that.items.length ? Object.keys(that.items[0]) : [];
        const fo = body.filter_options || {};
        that.payment_methods_from_api = fo.payment_methods || [];
        that.viewCounts[that.view_mode] = that.items.length;

        that.$bvToast.toast(
          `Loaded ${that.items.length} records`,
          { title: 'Success', autoHideDelay: 1200, variant: 'success' }
        );
      };
      const f = (err) => {
        that.items_being_loaded = false;
        console.error(err);
        that.$bvToast.toast('Error loading data.',
          { title: 'Error', variant: 'danger' });
      };

      this.items_being_loaded = true;
      this.items = [];
      Requestor.postSyncP('/api/reports/reports_stores_sale_v2/', payload, this, s, f);
    },

    // -------- confirm modal --------
    performAction(action) {
      const validIds = this.selectedInvoiceIds.filter(
        id => id != null && String(id).trim() !== ''
      );
      if (!validIds.length) {
        this.$bvToast.toast('No valid invoices selected.',
          { title: 'Error', variant: 'danger', solid: true });
        return;
      }

      const n = validIds.length;
      const configs = {
        archive: {
          title: 'Archive Invoices',
          icon: 'archive',
          variant: 'warning',
          okLabel: 'Archive',
          message: `Archive ${n} invoice${n === 1 ? '' : 's'}?`,
          subMessage: 'Archived invoices are hidden from Normal view but can be restored from the Archived tab.'
        },
        unarchive: {
          title: 'Unarchive Invoices',
          icon: 'arrow-counterclockwise',
          variant: 'success',
          okLabel: 'Unarchive',
          message: `Unarchive ${n} invoice${n === 1 ? '' : 's'}?`,
          subMessage: 'They will return to the Normal view.'
        },
        delete: {
          title: 'Delete Invoices',
          icon: 'trash',
          variant: 'danger',
          okLabel: 'Delete',
          message: `Delete ${n} invoice${n === 1 ? '' : 's'}?`,
          subMessage: 'Deleted invoices are hidden from Normal and Archived views. They can still be restored from the Deleted tab.'
        },
        restore: {
          title: 'Restore Invoices',
          icon: 'arrow-counterclockwise',
          variant: 'success',
          okLabel: 'Restore',
          message: `Restore ${n} invoice${n === 1 ? '' : 's'}?`,
          subMessage: 'They will return to the Normal view.'
        }
      };

      const cfg = configs[action];
      if (!cfg) return;

      this.confirmModal = {
        show: true,
        title: cfg.title,
        message: cfg.message,
        subMessage: cfg.subMessage,
        icon: cfg.icon,
        variant: cfg.variant,
        okLabel: cfg.okLabel,
        onConfirm: () => this.executeAction(action)
      };
    },

    executeAction(action) {
      const validIds = this.selectedInvoiceIds.filter(
        id => id != null && String(id).trim() !== ''
      );
      if (!validIds.length) {
        this.$bvToast.toast('No valid invoices selected.',
          { title: 'Error', variant: 'danger', solid: true });
        return;
      }

      const url = (action === 'archive' || action === 'unarchive')
        ? '/api/reports/invoices/archive/'
        : '/api/reports/invoices/delete/';

      const payload = {
        invoice_ids: validIds,
        action,
        username: this.packet.userDetails.username
      };

      const that = this;
      const s = (resp) => {
        if (resp.data.error) {
          that.$bvToast.toast(resp.data.error,
            { title: 'Error', variant: 'danger', solid: true });
          return;
        }
        that.$bvToast.toast(
          `${resp.data.updated} invoice(s) processed.`,
          { title: 'Success', variant: 'success', autoHideDelay: 2000, solid: true }
        );
        that.selectedInvoiceIds = [];
        that.fetch_init();
      };
      const f = () => {
        that.$bvToast.toast('Action failed. Please try again.',
          { title: 'Error', variant: 'danger', solid: true });
      };

      Requestor.postSyncP(url, payload, this, s, f);
    },

    // -------- export --------
    exportToCSV() {
      if (!this.filteredItems.length) return;
      const headers = this.allowed_cols().map(c => this.fixed_columns_dict[c] || c);
      const rows = this.filteredItems.map(item =>
        this.allowed_cols().map(c => {
          if (c === 'locsub') return this.packet.locsubsdict[item[c]] || item[c] || '';
          if (c === 'payment_methods') return (item[c] || []).join('|');
          if (c === 'is_delivered') return item[c] ? 'Delivered' : 'Not Delivered';
          if (this.isNumericColumn(c)) {
            const n = item[c] != null ? parseFloat(item[c]) : 0;
            return n.toFixed(2);
          }
          return item[c] != null ? item[c] : '';
        })
      );
      const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.getTableTitle().replace(/\s/g, '_')}_${moment().format('YYYY-MM-DD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  },

  created() {
    this.packet = JSON.parse(localStorage.packet || '{}');
    this.packet.userDetails = JSON.parse(localStorage.userDetails || '{}');
    this.packet.locsubsdict = this.packet.locsubsdict || {};
    this.packet.locations = this.packet.locations || [];

    this.details_filter = 1;
    this.drilled_location_id = null;
    this.drilled_location_label = null;
    this.selectedInvoiceIds = [];

    this.$nextTick(() => this.fetch_init());
  }
};
</script>

<style scoped>
/* ===================================================== */
/* FONTS / SHELL                                          */
/* ===================================================== */
.rsv2-page {
  position: relative;
  background: #F6F4F0;
  min-height: 100vh;
  padding: 18px 0 60px;
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: #221F1C;
  font-size: 15px;
}
.rsv2-page > * {
  max-width: 1800px;
  width: 97%;
  margin-left: auto;
  margin-right: auto;
}
@media (max-width: 767px) {
  .rsv2-page { padding: 10px 0 40px; }
  .rsv2-page > * { width: 100%; padding-left: 12px; padding-right: 12px; }
}

/* ===================================================== */
/* HERO                                                   */
/* ===================================================== */
.rsv2-hero {
  background: #221F1C;
  color: #fff;
  border-radius: 20px;
  overflow: hidden;
  border-bottom: 5px solid #EE7A22;
  margin-bottom: 14px;
  animation: rsv2SlideDown 0.5s ease-out;
}
@keyframes rsv2SlideDown {
  from { opacity: 0; transform: translateY(-12px); }
  to   { opacity: 1; transform: translateY(0); }
}
.rsv2-hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr);
  gap: 24px;
  padding: 10px 30px;
  align-items: center;
}
.rsv2-hero-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
}
.rsv2-hero-title {
  font-family: 'Sora', system-ui, sans-serif;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.1;
  margin: 6px 0 14px;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.rsv2-count-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #EE7A22;
  color: #fff;
  font-family: 'Sora', sans-serif;
  font-size: 17px;
  font-weight: 800;
  padding: 4px 16px;
  border-radius: 999px;
  animation: rsv2PopIn 0.35s cubic-bezier(0.34, 1.2, 0.64, 1);
}
@keyframes rsv2PopIn {
  from { transform: scale(0); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
}
.rsv2-hero-tags { display: flex; flex-wrap: wrap; gap: 8px; }
.rsv2-tag {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13.5px;
  font-weight: 700;
  padding: 6px 15px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.14);
}
.rsv2-tag.is-orange {
  background: rgba(238, 122, 34, 0.22);
  color: #FFB06B;
  border-color: rgba(238, 122, 34, 0.45);
}
.rsv2-tag.is-teal {
  background: rgba(15, 110, 107, 0.24);
  color: #7FDACF;
  border-color: rgba(15, 110, 107, 0.5);
}
.rsv2-hero-qr {
  display: flex;
  align-items: center;
  gap: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  padding: 14px 20px;
  color: #FFB06B;
}
.rsv2-qr-title { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 16px; color: #fff; }
.rsv2-qr-sub { font-size: 13.5px; color: rgba(255, 255, 255, 0.65); margin-top: 2px; }
@media (max-width: 900px) {
  .rsv2-hero-grid { grid-template-columns: 1fr; gap: 14px; }
  .rsv2-hero-title { font-size: 20px; }
}

/* ===================================================== */
/* CARDS                                                  */
/* ===================================================== */
.rsv2-card {
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 16px;
  padding: 14px 18px;
  margin-bottom: 12px;
  animation: rsv2FadeUp 0.4s ease-out both;
}
@keyframes rsv2FadeUp {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

.rsv2-viewcard { padding: 16px 20px; }
.rsv2-view-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}
.rsv2-view-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.rsv2-label {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #736C63;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.rsv2-pillgroup {
  display: inline-flex;
  background: #F1EEE9;
  border-radius: 12px;
  padding: 4px;
  gap: 4px;
}
.rsv2-pill-btn {
  border: 0;
  background: transparent;
  padding: 9px 18px;
  border-radius: 9px;
  font-weight: 700;
  font-size: 14px;
  color: #4A4540;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.15s ease;
}
.rsv2-pill-btn:hover { color: #EE7A22; }
.rsv2-pill-btn.is-active {
  background: #EE7A22;
  color: #fff;
  box-shadow: 0 4px 12px -6px rgba(238, 122, 34, 0.6);
}
.rsv2-pill-btn.is-active-dark {
  background: #221F1C;
  color: #fff;
}
.rsv2-pill-count {
  background: rgba(255, 255, 255, 0.22);
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 800;
  min-width: 20px;
  display: inline-block;
  text-align: center;
}
.rsv2-pill-btn:not(.is-active):not(.is-active-dark) .rsv2-pill-count {
  background: #E5E1DA;
  color: #4A4540;
}

.rsv2-drillbar {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #E5E1DA;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.rsv2-hintline {
  margin-top: 10px;
  font-size: 13.5px;
  color: #736C63;
  display: flex;
  align-items: center;
  gap: 8px;
}

.rsv2-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 800;
  padding: 5px 13px;
  border-radius: 999px;
  background: #F1EEE9;
  color: #4A4540;
  border: 1px solid #E5E1DA;
  white-space: nowrap;
}
.rsv2-chip.is-orange { background: #FFF1E4; color: #D4620F; border-color: #F5CFAE; }
.rsv2-chip.is-teal   { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }
.rsv2-chip.is-warn   { background: #FFF4DC; color: #8A5E09; border-color: #F2DBA5; }
.rsv2-chip.is-danger { background: #FBE9E7; color: #C93C30; border-color: #F3C9C3; }

.rsv2-linkbtn {
  background: none;
  border: 0;
  color: #D4620F;
  font-weight: 700;
  font-size: 13.5px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}
.rsv2-linkbtn:hover { color: #EE7A22; text-decoration: underline; }

/* ===================================================== */
/* FILTERS                                                */
/* ===================================================== */
.rsv2-filtercard { padding: 16px 20px; }
.rsv2-filters-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.rsv2-fb-dates { flex: 0 1 380px; min-width: 300px; }

.rsv2-inputgroup { display: flex; flex-wrap: nowrap; width: 100%; }
.rsv2-inputgroup >>> .input-group-text {
  height: 46px;
  padding: 0 14px;
  background: #F1EEE9;
  border: 1.5px solid #E5E1DA;
  border-right: 0;
  border-radius: 10px 0 0 10px !important;
  color: #736C63;
  font-size: 15px;
}
.rsv2-inputgroup >>> .rsv2-date-input {
  height: 46px !important;
  border: 1.5px solid #E5E1DA !important;
  border-radius: 0 10px 10px 0 !important;
  background: #F7F5F2 !important;
  padding: 0 14px !important;
  font-size: 14px !important;
  font-weight: 600 !important;
}
.rsv2-inputgroup >>> .rsv2-date-input:focus {
  border-color: #EE7A22 !important;
  background: #fff !important;
  box-shadow: none !important;
}

.rsv2-quickdates {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.rsv2-quick-btn {
  background: #F7F5F2;
  border: 1.5px solid #E5E1DA;
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 12.5px;
  font-weight: 700;
  color: #4A4540;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.rsv2-quick-btn:hover { border-color: #EE7A22; color: #D4620F; }
.rsv2-quick-btn.is-active {
  background: #EE7A22;
  border-color: #EE7A22;
  color: #fff;
}

.rsv2-search-btn { margin-left: auto; }
.rsv2-btn-primary,
.rsv2-btn-primary.btn {
  background: #EE7A22;
  border: 1.5px solid #EE7A22;
  color: #fff;
  border-radius: 10px;
  font-weight: 700;
  font-size: 14px;
  padding: 10px 20px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.rsv2-btn-primary:hover:not(:disabled) {
  background: #D4620F;
  border-color: #D4620F;
  transform: translateY(-1px);
}
.rsv2-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.rsv2-btn-primary.is-danger { background: #C93C30; border-color: #C93C30; }
.rsv2-btn-primary.is-danger:hover { background: #A22A1F; border-color: #A22A1F; }
.rsv2-btn-primary.is-warning { background: #C98A12; border-color: #C98A12; }
.rsv2-btn-primary.is-warning:hover { background: #A97708; border-color: #A97708; }
.rsv2-btn-primary.is-success { background: #0F6E6B; border-color: #0F6E6B; }
.rsv2-btn-primary.is-success:hover { background: #0B5552; border-color: #0B5552; }

.rsv2-btn-ghost {
  background: #fff;
  border: 1.5px solid #E5E1DA;
  color: #221F1C;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13.5px;
  padding: 9px 16px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.rsv2-btn-ghost:hover { border-color: #EE7A22; color: #D4620F; }

.rsv2-btn-warn {
  background: #C98A12;
  border: 1.5px solid #C98A12;
  color: #fff;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13.5px;
  padding: 9px 16px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
}
.rsv2-btn-warn:hover { background: #A97708; border-color: #A97708; }

.rsv2-btn-danger {
  background: #C93C30;
  border: 1.5px solid #C93C30;
  color: #fff;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13.5px;
  padding: 9px 16px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
}
.rsv2-btn-danger:hover { background: #A22A1F; border-color: #A22A1F; }

.rsv2-btn-success {
  background: #0F6E6B;
  border: 1.5px solid #0F6E6B;
  color: #fff;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13.5px;
  padding: 9px 16px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
}
.rsv2-btn-success:hover { background: #0B5552; border-color: #0B5552; }

.rsv2-advanced-toggle-wrap { margin-top: 10px; }
.rsv2-advanced-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed #E5E1DA;
  animation: rsv2FadeUp 0.35s ease-out;
}
@media (max-width: 992px) { .rsv2-advanced-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 576px) { .rsv2-advanced-grid { grid-template-columns: 1fr; } }

.rsv2-adv-item { display: flex; flex-direction: column; gap: 8px; }
.rsv2-select {
  height: 46px !important;
  border-radius: 10px !important;
  border: 1.5px solid #E5E1DA !important;
  background: #F7F5F2 !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #221F1C !important;
}
.rsv2-select:focus { border-color: #EE7A22 !important; box-shadow: none !important; }

/* ===================================================== */
/* KPI CARDS                                              */
/* ===================================================== */
.rsv2-kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}
.rsv2-kpi {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border: 1px solid #E5E1DA;
  border-left: 6px solid transparent;
  border-radius: 14px;
  padding: 14px 16px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.rsv2-kpi:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px -16px rgba(34, 31, 28, 0.4);
}
.rsv2-kpi-icon {
  width: 48px; height: 48px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 22px; flex-shrink: 0;
}
.rsv2-kpi-body { display: flex; flex-direction: column; min-width: 0; }
.rsv2-kpi-value {
  font-family: 'Sora', sans-serif;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.01em;
}
.rsv2-kpi-label { font-size: 13.5px; color: #736C63; font-weight: 600; margin-top: 2px; }
.rsv2-kpi.is-total   { border-left-color: #D4620F; }
.rsv2-kpi.is-total   .rsv2-kpi-icon { background: linear-gradient(135deg, #EE7A22, #D4620F); }
.rsv2-kpi.is-total   .rsv2-kpi-value { color: #D4620F; }
.rsv2-kpi.is-refund  { border-left-color: #C93C30; }
.rsv2-kpi.is-refund  .rsv2-kpi-icon { background: linear-gradient(135deg, #C93C30, #A22A1F); }
.rsv2-kpi.is-refund  .rsv2-kpi-value { color: #C93C30; }
.rsv2-kpi.is-count   { border-left-color: #0F6E6B; }
.rsv2-kpi.is-count   .rsv2-kpi-icon { background: linear-gradient(135deg, #0F6E6B, #0B5552); }
.rsv2-kpi.is-count   .rsv2-kpi-value { color: #0F6E6B; }
.rsv2-kpi.is-layaway { border-left-color: #C98A12; }
.rsv2-kpi.is-layaway .rsv2-kpi-icon { background: linear-gradient(135deg, #E0A524, #C98A12); }
.rsv2-kpi.is-layaway .rsv2-kpi-value { color: #C98A12; }
@media (max-width: 991px) { .rsv2-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px) { .rsv2-kpi-grid { grid-template-columns: 1fr; } }

/* ===================================================== */
/* ACTION BAR                                             */
/* ===================================================== */
.rsv2-actionbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  background: #FFF1E4;
  border: 1px solid #F5CFAE;
  border-radius: 14px;
  padding: 12px 18px;
  margin-bottom: 12px;
  animation: rsv2FadeUp 0.35s ease-out;
}
.rsv2-actionbar-left, .rsv2-actionbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* ===================================================== */
/* TABLE                                                  */
/* ===================================================== */
.rsv2-tablecard { padding: 0; overflow: hidden; }
.rsv2-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid #F1EEE9;
  flex-wrap: wrap;
  gap: 10px;
}
.rsv2-table-title {
  font-family: 'Sora', sans-serif;
  font-size: 17px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
}
.rsv2-table-badge {
  font-size: 11.5px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 999px;
  background: #F1EEE9;
  color: #4A4540;
  border: 1px solid #E5E1DA;
  letter-spacing: 0.02em;
}
.rsv2-table-actions { display: flex; gap: 8px; flex-wrap: wrap; }

.rsv2-table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.rsv2-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
  min-width: 720px;
}
.rsv2-th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #221F1C;
  color: #fff;
  text-align: left;
  font-family: 'Sora', sans-serif;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 14px 14px;
  white-space: nowrap;
  border-bottom: 3px solid #EE7A22;
}
.rsv2-th-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.rsv2-th.is-right .rsv2-th-inner { justify-content: flex-end; }
.rsv2-sort-btn {
  background: none;
  border: 0;
  color: rgba(255, 255, 255, 0.55);
  padding: 0;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  transition: color 0.15s ease;
}
.rsv2-sort-btn:hover { color: #FFB06B; }
.rsv2-sort-btn .b-icon.is-active { color: #FFB06B; }
.rsv2-th-check { width: 46px; text-align: center; padding: 12px 6px; }

.rsv2-row td {
  padding: 13px 14px;
  border-bottom: 1px solid #F1EEE9;
  vertical-align: top;
  background: #fff;
}
.rsv2-row {
  animation: rsv2RowIn 0.35s ease-out backwards;
  transition: background 0.15s ease;
}
@keyframes rsv2RowIn {
  from { opacity: 0; transform: translateX(-4px); }
  to   { opacity: 1; transform: translateX(0); }
}
.rsv2-row:hover td { background: #FAF9F7; }
.rsv2-row.is-clickable { cursor: pointer; }
.rsv2-row.is-clickable:hover td { background: #FFF1E4; }
.rsv2-row.is-danger-row td { background: #FFF6F4; }
.rsv2-row.is-danger-row:hover td { background: #FEEFEC; }
.rsv2-row.is-teal-row td { background: #F2FBFA; }
.rsv2-row.is-teal-row:hover td { background: #E2F2F0; }
.rsv2-td { color: #221F1C; }
.rsv2-td.is-right { text-align: right; }
.rsv2-td.is-center { text-align: center; }
.rsv2-td.is-danger .rsv2-num { color: #C93C30; font-weight: 700; }
.rsv2-td.is-teal .rsv2-num { color: #0F6E6B; font-weight: 700; }
.rsv2-td-check { width: 46px; text-align: center; padding: 13px 6px; }
.rsv2-num { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 18px; }

.rsv2-loc-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
}
.rsv2-loc-icon { color: #EE7A22; font-size: 13px; }
.rsv2-drill-icon {
  color: #EE7A22;
  margin-left: 4px;
  font-size: 12px;
  transition: transform 0.15s ease;
}
.rsv2-row.is-clickable:hover .rsv2-drill-icon { transform: translateX(3px); }

/* NEW: invoice link style */
.rsv2-invoice-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'Sora', sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: #D4620F;
  text-decoration: none;
  border-bottom: 1px dashed rgba(212, 98, 15, 0.4);
  padding-bottom: 1px;
  transition: color 0.15s ease, border-color 0.15s ease;
}
.rsv2-invoice-link:hover {
  color: #EE7A22;
  border-color: #EE7A22;
  text-decoration: none;
}
.rsv2-invoice-link-icon {
  font-size: 11px;
  opacity: 0.65;
  transition: transform 0.15s ease, opacity 0.15s ease;
}
.rsv2-invoice-link:hover .rsv2-invoice-link-icon {
  opacity: 1;
  transform: translate(1px, -1px);
}

.rsv2-mini-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.03em;
  padding: 3px 9px;
  border-radius: 999px;
  background: #F1EEE9;
  color: #4A4540;
  border: 1px solid #E5E1DA;
  margin-right: 4px;
  text-transform: uppercase;
}
.rsv2-status-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 11px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.rsv2-status-chip.is-teal { background: #E2F2F0; color: #0F6E6B; }
.rsv2-status-chip.is-warn { background: #FFF4DC; color: #8A5E09; }
.rsv2-muted { color: #A59D92; font-style: italic; font-size: 13px; }

.rsv2-totals-row td {
  background: #FFF1E4;
  padding: 14px;
  border-top: 2px solid #F5CFAE;
  font-weight: 800;
}
.rsv2-totals-label {
  font-family: 'Sora', sans-serif;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #D4620F;
}
.rsv2-totals-amt {
  font-family: 'Sora', sans-serif;
  font-size: 15px;
  font-weight: 800;
  color: #D4620F;
}

.rsv2-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  border-top: 1px solid #F1EEE9;
  background: #FAF9F7;
  flex-wrap: wrap;
}
.rsv2-page-info { font-size: 13px; color: #736C63; font-weight: 600; }

/* ===================================================== */
/* LOADING / EMPTY                                        */
/* ===================================================== */
.rsv2-loading-inline {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #736C63;
  font-weight: 600;
  font-size: 15px;
  padding: 40px 20px;
}
.rsv2-empty {
  text-align: center;
  padding: 60px 30px;
  border: 2px dashed #D6CFC4;
  background: #FAF9F7;
}
.rsv2-empty-icon {
  width: 78px; height: 78px; border-radius: 22px;
  background: #FFF1E4; color: #EE7A22;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 34px; margin-bottom: 18px;
}
.rsv2-empty h3 { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 700; margin: 0 0 8px; }
.rsv2-empty p { color: #736C63; max-width: 50ch; margin: 0 auto; line-height: 1.6; font-size: 15px; }

/* ===================================================== */
/* MODAL                                                  */
/* ===================================================== */
.rsv2-modal.modal-content {
  border: 0;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 30px 70px -20px rgba(34, 31, 28, 0.5);
  animation: rsv2ModalIn 0.3s cubic-bezier(0.34, 1.2, 0.64, 1);
}
@keyframes rsv2ModalIn {
  from { transform: scale(0.94); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
}
.rsv2-modal .modal-header {
  background: #221F1C !important;
  color: #fff !important;
  border: 0 !important;
  border-bottom: 4px solid #EE7A22 !important;
  padding: 18px 26px !important;
}
.rsv2-modal .modal-header .close { color: #fff !important; opacity: 0.85; text-shadow: none; }
.rsv2-modal .modal-body { background: #F6F4F0; padding: 24px; }
.rsv2-modal .modal-footer {
  background: #FAF9F7;
  border-top: 1px solid #E5E1DA;
  padding: 14px 22px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.rsv2-modal-title { display: flex; align-items: center; gap: 16px; }
.rsv2-modal-title-icon {
  width: 48px; height: 48px; border-radius: 12px;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 22px;
}
.rsv2-modal-title-icon.is-warning { background: #FFF4DC; color: #A97708; }
.rsv2-modal-title-icon.is-danger  { background: #FBE9E7; color: #C93C30; }
.rsv2-modal-title-icon.is-success { background: #E2F2F0; color: #0F6E6B; }
.rsv2-modal-title-icon.is-primary { background: #FFF1E4; color: #D4620F; }
.rsv2-modal-title-main { font-family: 'Sora', sans-serif; font-size: 20px; font-weight: 700; }
.rsv2-modal-title-sub { font-size: 13px; color: rgba(255, 255, 255, 0.65); }
.rsv2-modal-body { display: flex; flex-direction: column; gap: 12px; }
.rsv2-modal-message { font-size: 16px; font-weight: 700; color: #221F1C; margin: 0; }
.rsv2-modal-sub { font-size: 14px; color: #736C63; line-height: 1.5; margin: 0; }

/* ===================================================== */
/* TRANSITIONS                                            */
/* ===================================================== */
.rsv2-fade-enter-active, .rsv2-fade-leave-active { transition: opacity 0.25s ease; }
.rsv2-fade-enter, .rsv2-fade-leave-to { opacity: 0; }
.rsv2-fade-up-enter-active { animation: rsv2FadeUp 0.4s ease-out; }
.rsv2-fade-up-enter, .rsv2-fade-up-leave-to { opacity: 0; }

@media (prefers-reduced-motion: reduce) {
  .rsv2-hero,
  .rsv2-row,
  .rsv2-count-pill,
  .rsv2-modal.modal-content,
  .rsv2-card { animation: none !important; }
}
</style>


// WEBPACK FOOTER //
// src/components/Reports/ReportsStoresSaleV2.vue